import { type InteractionChannelEnum } from '@/common/types/channel'
import type { Case } from '@/common/types/case'
import { getInlineIcon } from '@/common/helpers/icon'
import { type FieldLocalizedMessage } from '@/admin/types/locale'
import {
  faBookOpenCover,
  faBriefcase,
  faBuilding,
  faCalendarDays,
  faCircle,
  faCloud,
  faDiamond,
  faEnvelopeOpenText,
  faEnvelopesBulk,
  faFax,
  faInboxIn,
  faInboxOut,
  faLaptopArrowDown,
  faMailboxFlagUp,
  faMicrophone,
  faNetworkWired,
  faNotebook,
  faPenNib,
  faPeopleGroup,
  faTypewriter,
  faUsbDrive,
  faVideo,
  faWaveformLines,
} from '@fortawesome/pro-regular-svg-icons'
import { EntityFieldCodeEnum, type CustomFieldValueData } from '@/common/types/fields'
import { MainEntityFieldInjectionMethods } from '@/common/types/general'

export const enum InteractionTypeEnum {
  MAIL_IN = 'MAIL_IN',
  INSTAGRAM_MEDIA = 'INSTAGRAM_MEDIA',
  INSTAGRAM_IN_COMMENT = 'INSTAGRAM_IN_COMMENT',
  INSTAGRAM_OUT_COMMENT = 'INSTAGRAM_OUT_COMMENT',
  INSTAGRAM_DM_IN = 'INSTAGRAM_DM_IN',
  CHAT_IN_NEW_USER_MESSAGE = 'CHAT_IN_NEW_USER_MESSAGE',
  CHAT_IN_INTERNAL_MESSAGE = 'CHAT_IN_INTERNAL_MESSAGE',
  CALL_IN = 'CALL_IN',
  INSTAGRAM_DM_OUT = 'INSTAGRAM_DM_OUT',
  SMS_OUT_NOTIFICATION = 'SMS_OUT_NOTIFICATION',
  INSTAGRAM_MENTIONED_COMMENT = 'INSTAGRAM_MENTIONED_COMMENT',
  INSTAGRAM_MENTIONED_MEDIA = 'INSTAGRAM_MENTIONED_MEDIA',
  INSTAGRAM_DM_STORY_MENTION = 'INSTAGRAM_DM_STORY_MENTION',
  INSTAGRAM_DM_STORY_REPLY = 'INSTAGRAM_DM_STORY_REPLY',
  INSTAGRAM_MEDIA_VIDEO = 'INSTAGRAM_MEDIA_VIDEO',
  INSTAGRAM_MENTIONED_MEDIA_VIDEO = 'INSTAGRAM_MENTIONED_MEDIA_VIDEO',
  MAIL_OUT_ACKNOWLEDGEMENT = 'MAIL_OUT_ACKNOWLEDGEMENT',
  MAIL_OUT_USER_REPLY = 'MAIL_OUT_USER_REPLY',
  MAIL_OUT_USER_FORWARD = 'MAIL_OUT_USER_FORWARD',
  MAIL_OUT_USER_INITIATED = 'MAIL_OUT_USER_INITIATED',
  CHAT_OUT_OPERATOR_RESPONSE = 'CHAT_OUT_OPERATOR_RESPONSE',
  CHAT_OUT_BOT_RESPONSE = 'CHAT_OUT_BOT_RESPONSE',
  CHAT_OUT_MANUAL_TRANSFER = 'CHAT_OUT_MANUAL_TRANSFER',
  CHAT_OUT_AUTO_TRANSFER = 'CHAT_OUT_AUTO_TRANSFER',
  SATISFACTION_CONTACT = 'SATISFACTION_CONTACT',
  CHAT_OUT_BOT_AUTO_RESPONSE = 'CHAT_OUT_BOT_AUTO_RESPONSE',
  CALL_OUT = 'CALL_OUT',
  FB_IN_COMMENT = 'FB_IN_COMMENT',
  FB_OUT_COMMENT = 'FB_OUT_COMMENT',
  MESSENGER_IN_MESSAGE = 'MESSENGER_IN_MESSAGE',
  MESSENGER_OUT_MESSAGE = 'MESSENGER_OUT_MESSAGE',
  SMS_IN = 'SMS_IN',
  SMS_OUT = 'SMS_OUT',
  FB_IN_POST = 'FB_IN_POST',
  TWITTER_IN = 'TWITTER_IN',
  TWITTER_OUT = 'TWITTER_OUT',
  TWITTER_DM_IN = 'TWITTER_DM_IN',
  TWITTER_DM_OUT = 'TWITTER_DM_OUT',
  MAIL_OUT_BULK_USER_INITIATED = 'MAIL_OUT_BULK_USER_INITIATED',
  WHATSAPP_IN = 'WHATSAPP_IN',
  WHATSAPP_OUT = 'WHATSAPP_OUT',
  MAIL_OUT_NOTIFICATION = 'MAIL_OUT_NOTIFICATION',
  FB_OUT_POST = 'FB_OUT_POST',
  VISIT = 'VISIT',
  MISSED_OPPORTUNITY = 'MISSED_OPPORTUNITY',
  OPPORTUNITY = 'OPPORTUNITY',
  CHAT_START = 'CHAT_START',
  CHAT_END = 'CHAT_END',
  MAIL_OUT_TRANSCRIPT = 'MAIL_OUT_TRANSCRIPT',
  WHATSAPP_OUT_TEMPLATE = 'WHATSAPP_OUT_TEMPLATE',
  MESSENGER_OUT_TEMPLATE = 'MESSENGER_OUT_TEMPLATE',
  CHAT_START_BOT = 'CHAT_START_BOT',
  CHAT_END_BOT = 'CHAT_END_BOT',
  MAIL_IN_DISSUASION = 'MAIL_IN_DISSUASION',
  MAIL_OUT_DISSUASION = 'MAIL_OUT_DISSUASION',
  WHATSAPP_IN_DISSUASION = 'WHATSAPP_IN_DISSUASION',
  WHATSAPP_OUT_DISSUASION = 'WHATSAPP_OUT_DISSUASION',
  API_IN = 'API_IN',
  API_OUT = 'API_OUT',
  EXTERNAL = 'EXTERNAL',
}

export const enum InteractionDirectionEnum {
  IN = 'IN',
  OUT = 'OUT',
}

export type InteractionTypeDTOV1 = {
  code: InteractionTypeEnum
  direction: InteractionDirectionEnum
  channel: InteractionChannelEnum
  active: boolean
}

export enum InteractionStatusEnum {
  OPEN = 'OPEN',
  REPLIED = 'REPLIED',
  BULKREPLIED = 'BULKREPLIED',
  FORWARDED = 'FORWARDED',
  NOACTIONREQUIRED = 'NOACTIONREQUIRED',
  COMPLETED = 'COMPLETED',
  DRAFT = 'DRAFT',
  TOSEND = 'TOSEND',
  TORESEND = 'TORESEND',
  SENT = 'SENT',
  SENDERROR = 'SENDERROR',
  TEMP = 'TEMP',
  TOBEVALIDATED = 'TOBEVALIDATED',
  MISSED = 'MISSED',
  UPDATED = 'UPDATED',
  DELETED = 'DELETED',
}

export type InteractionDTOV4 = {
  id: number
  channel: InteractionChannelEnum
  parentId?: number
  direction: InteractionDirectionEnum
  summary?: string
  content?: string
  displayIcon?: InteractionCustomIcon
  type: InteractionTypeEnum
  customTypeId?: UUID
  status: InteractionStatusEnum
  statusDate?: string
  interactionDate: string
  interactionUser?: string
  dueDate?: string
  sentFrom?: string
  sentTo?: string
  caseId?: number
  customerId?: number
  // include HAS_ATTACHMENTS
  hasAttachments?: boolean
  // include CUSTOM_FIELDS
  customFields?: CustomFieldValueData[]
}

// Bulk fetch V1
export type InteractionDetailsDTOV1 = {
  id: number
  channelCode: InteractionChannelEnum
  parentId?: number
  directionCode: InteractionDirectionEnum
  summary?: string
  displayIcon?: InteractionCustomIcon
  statusCode: InteractionStatusEnum
  statusDate?: string
  interactionDate: string
  userLogin?: string
  dueDate?: string
  sentFrom?: string
  sentTo?: string
  caseId?: number
  customerId?: number
  typeCode: InteractionTypeEnum
  customTypeId?: UUID
  // include HAS_ATTACHMENTS
  hasAttachments?: boolean
  // expand CUSTOM_FIELDS
  customFields?: CustomFieldValueData[]
}

export type Interaction = InteractionDetailsDTOV1 & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
  // by fetching /api/v1/bulk-cases/fetch
  case?: Case
}

export enum InteractionIncludeEnum {
  HAS_ATTACHMENTS = 'HAS_ATTACHMENTS',
}

export enum InteractionExpandEnum {
  CUSTOM_FIELDS = 'CUSTOM_FIELDS',
}

type InteractionAvailableIncludeOptions = keyof typeof InteractionIncludeEnum
type InteractionAvailableExpandOptions = keyof typeof InteractionExpandEnum

type InteractionFieldInjectionMethod<T> = T extends InteractionAvailableIncludeOptions
  ? (typeof MainEntityFieldInjectionMethods)['INCLUDE']
  : (typeof MainEntityFieldInjectionMethods)['EXPAND']

type InteractionFieldInjectionOption<T extends InteractionAvailableIncludeOptions | InteractionAvailableExpandOptions> = {
  scope: T
  method: InteractionFieldInjectionMethod<T>
}

/**
 * Map of interaction fields added by an include and an expand
 */
export const InteractionFieldInjectionMap = new Map<
  EntityFieldCodeEnum,
  InteractionFieldInjectionOption<InteractionAvailableIncludeOptions> | InteractionFieldInjectionOption<InteractionAvailableExpandOptions>
>([
  [
    EntityFieldCodeEnum.INTERACTION_HAS_ATTACHMENTS,
    { scope: InteractionIncludeEnum.HAS_ATTACHMENTS, method: MainEntityFieldInjectionMethods.INCLUDE },
  ],
])

export type InteractionFieldConfig = {
  includes?: Set<InteractionIncludeEnum>
  expands?: Set<InteractionExpandEnum>
}

export type InteractionDTOV3 = {
  id: number
  parentId?: number
  direction?: InteractionDirectionEnum
  summary?: string
  content?: string
  status: InteractionStatusEnum
  statusDate?: string
  creationDate?: string
  interactionUser: number
  dueDate?: string
  from?: string
  to?: string
  caseId?: number
  customerId?: number
  customInteractionTypeId?: string
  aht: number
}

export type InteractionStatusDTOV3 = {
  status: InteractionStatusEnum
  statusDate: string
}

export type DisplayApiInteractionModalEvent = {
  id: number
  status: InteractionStatusEnum
}

export type InteractionUpdateDTOV3 = Omit<InteractionDTOV3, 'id'>

export const InteractionCustomIcons = {
  NOTEBOOK: 'notebook',
  INBOX_OUT: 'inbox-out',
  INBOX_IN: 'inbox-in',
  MAILBOX_FLAG_UP: 'mailbox-flag-up',
  TYPEWRITER: 'typewriter',
  ENVELOPE_OPEN_TEXT: 'envelope-open-text',
  PEOPLE_GROUP: 'people-group',
  BUILDING: 'building',
  BRIEFCASE: 'briefcase',
  PEN_NIB: 'pen-nib',
  CALENDAR_DAYS: 'calendar-days',
  NETWORK_WIRED: 'network-wired',
  VIDEO: 'video',
  USB_DRIVE: 'usb-drive',
  FAX: 'fax',
  LAPTOP_ARROW_DOWN: 'laptop-arrow-down',
  MICROPHONE: 'microphone',
  WAVEFORM_LINES: 'waveform-lines',
  BOOK_OPEN_COVER: 'book-open-cover',
  ENVELOPES_BULK: 'envelopes-bulk',
  CIRCLE: 'circle',
  CLOUD: 'cloud',
  DIAMOND: 'diamond',
} as const

export const enum InteractionModalType {
  CUSTOM = 'CUSTOM',
  API = 'API',
}

export type InteractionCustomIcon = (typeof InteractionCustomIcons)[keyof typeof InteractionCustomIcons]

export interface InteractionCustomDTOV3 {
  id: UUID
  active: boolean
  direction: InteractionDirectionEnum
  displayIcon: InteractionCustomIcon
}

export type InteractionCustom = InteractionCustomDTOV3 & {
  name: string
}

export type InteractionCustomForm = Omit<InteractionCustomDTOV3, 'id'> & {
  translations: FieldLocalizedMessage[]
}

export type InteractionCustomCreateOrUpdate = Omit<InteractionCustomDTOV3, 'id'>

export const getIconDefinitionFromInteractionCustomIcon = (iconName: InteractionCustomIcon | undefined) => {
  switch (iconName) {
    case InteractionCustomIcons.NOTEBOOK:
      return getInlineIcon(faNotebook)
    case InteractionCustomIcons.INBOX_OUT:
      return getInlineIcon(faInboxOut)
    case InteractionCustomIcons.INBOX_IN:
      return getInlineIcon(faInboxIn)
    case InteractionCustomIcons.MAILBOX_FLAG_UP:
      return getInlineIcon(faMailboxFlagUp)
    case InteractionCustomIcons.TYPEWRITER:
      return getInlineIcon(faTypewriter)
    case InteractionCustomIcons.ENVELOPE_OPEN_TEXT:
      return getInlineIcon(faEnvelopeOpenText)
    case InteractionCustomIcons.PEOPLE_GROUP:
      return getInlineIcon(faPeopleGroup)
    case InteractionCustomIcons.BUILDING:
      return getInlineIcon(faBuilding)
    case InteractionCustomIcons.BRIEFCASE:
      return getInlineIcon(faBriefcase)
    case InteractionCustomIcons.PEN_NIB:
      return getInlineIcon(faPenNib)
    case InteractionCustomIcons.CALENDAR_DAYS:
      return getInlineIcon(faCalendarDays)
    case InteractionCustomIcons.NETWORK_WIRED:
      return getInlineIcon(faNetworkWired)
    case InteractionCustomIcons.VIDEO:
      return getInlineIcon(faVideo)
    case InteractionCustomIcons.USB_DRIVE:
      return getInlineIcon(faUsbDrive)
    case InteractionCustomIcons.FAX:
      return getInlineIcon(faFax)
    case InteractionCustomIcons.LAPTOP_ARROW_DOWN:
      return getInlineIcon(faLaptopArrowDown)
    case InteractionCustomIcons.MICROPHONE:
      return getInlineIcon(faMicrophone)
    case InteractionCustomIcons.WAVEFORM_LINES:
      return getInlineIcon(faWaveformLines)
    case InteractionCustomIcons.BOOK_OPEN_COVER:
      return getInlineIcon(faBookOpenCover)
    case InteractionCustomIcons.ENVELOPES_BULK:
      return getInlineIcon(faEnvelopesBulk)
    case InteractionCustomIcons.CIRCLE:
      return getInlineIcon(faCircle)
    case InteractionCustomIcons.CLOUD:
      return getInlineIcon(faCloud)
    case InteractionCustomIcons.DIAMOND:
      return getInlineIcon(faDiamond)
    default:
      return getInlineIcon(faNotebook)
  }
}
